"use client";

// ============================================================
// CLUBHUB – Zusagen eines Vorkommens
// ============================================================
// Wer kommt, wer nicht, wer noch nicht geantwortet hat — mit
// Kommentar und Zeitpunkt. Die Zahlen oben SIND der Filter (wie im
// Listen-Baukasten): „Offen 5“ zeigt die fuenf.
//
// Die Uebungsleitung traegt Antworten auch fuer andere ein (die
// telefonische Absage, das Kind ohne Telefon): drei Knoepfe je Zeile.
// Mit Hoechstzahl rueckt beim Absagen automatisch die erste Person
// der Warteliste nach (die Reihenfolge der Zusagen entscheidet, siehe
// `lib/kalender/zusagen.ts`). Gespeichert wird jeder Tipp sofort
// (`zusageSetzen`); geht es schief, springt die Zeile zurueck.
//
// DATENSCHUTZ: wer der Veroeffentlichung im Internet nicht
// zugestimmt hat, traegt ein Zeichen — er erscheint in keiner
// oeffentlichen Ausgabe dieser Liste.
// ============================================================
import { useMemo, useState } from "react";
import Link from "next/link";
import { BellRing, Check, Download, EyeOff, HelpCircle, Search, UserPlus, X } from "lucide-react";
import { Schiene, Zaehlknopf, Fuge, KARTENRAND, LISTENKARTE, Kartenstapel } from "@/components/liste";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogCancel, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { Tooltip } from "@/components/ui/tooltip";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { RSVP_STATUS } from "@/lib/labels";
import { formatTagMitWochentag, relativTage, tagVon, uhrzeit, type DayKey } from "@/lib/kalender/tag";
import { zusagenAuswerten } from "@/lib/kalender/zusagen";
import type { AntwortView, PersonKurz } from "@/lib/kalender/views";
import type { RsvpStatus } from "@/generated/prisma/enums";
import { mockPersonen } from "@/lib/mock/kalender";
import { errorOf } from "@/lib/action-result";
import type { ContactView } from "@/lib/views";
import { personenEinladen, zusageSetzen } from "@/app/(app)/kalender/actions";
import { cn } from "@/lib/utils";

type Fach = "dabei" | "warteliste" | "vielleicht" | "offen" | "absage";

const FAECHER: { key: Fach; label: string }[] = [
  { key: "dabei", label: "Zusagen" },
  { key: "warteliste", label: "Warteliste" },
  { key: "vielleicht", label: "Vielleicht" },
  { key: "offen", label: "Noch offen" },
  { key: "absage", label: "Absagen" },
];

export function ZusagenListe({
  titel,
  tagText,
  eingeladene: anfangsPersonen,
  antworten: anfang,
  max,
  vielleichtErlaubt,
  frist,
  jetzt,
  heute,
  abgesagt,
  occurrence,
  eventId,
  kontakte,
  nurLesen = false,
}: {
  eventId: string;
  /** Ohne Recht „Termine bearbeiten“: ansehen, nicht eintragen. */
  nurLesen?: boolean;
  /** Personen fuer „Einladen“ (im Musterbestand leer: Vorgabe der Auswahl). */
  kontakte?: ContactView[];
  titel: string;
  tagText: string;
  eingeladene: PersonKurz[];
  antworten: AntwortView[];
  max: number | null;
  vielleichtErlaubt: boolean;
  frist: string;
  jetzt: string;
  heute: DayKey;
  abgesagt: boolean;
  occurrence: string | null;
}) {
  const [personen, setPersonen] = useState(anfangsPersonen);
  const [antworten, setAntworten] = useState(anfang);
  const [fach, setFach] = useState<Fach | "">("");
  const [suche, setSuche] = useState("");
  const [einladen, setEinladen] = useState(false);

  const auswertung = useMemo(() => zusagenAuswerten(antworten, personen.map((p) => p.id), max), [antworten, personen, max]);
  const z = auswertung.zaehler;
  const fristVorbei = frist < jetzt;

  function fachVon(id: string): Fach {
    const a = auswertung.jePerson.get(id);
    if (!a || a.status === "PENDING") return "offen";
    if (a.status === "NO") return "absage";
    if (a.status === "MAYBE") return "vielleicht";
    return auswertung.dabei.has(id) ? "dabei" : "warteliste";
  }

  const zahl: Record<Fach, number> = { dabei: z.yes, warteliste: z.warteliste, vielleicht: z.maybe, offen: z.pending, absage: z.no };

  const gezeigt = personen.filter((p) => (!fach || fachVon(p.id) === fach) && (!suche || p.name.toLowerCase().includes(suche.toLowerCase())));

  async function setzen(p: PersonKurz, status: RsvpStatus) {
    const vorher = auswertung.jePerson.get(p.id);
    const warVorne = auswertung.dabei.has(p.id);
    const ersterWartend = auswertung.warteliste[0];
    const sicherung = antworten;
    setAntworten((liste) => {
      const rest = liste.filter((a) => a.contactId !== p.id);
      return [
        ...rest,
        {
          id: vorher?.id ?? `a-neu-${p.id}`,
          contactId: p.id,
          occurrence,
          status,
          comment: vorher?.comment ?? null,
          attended: vorher?.attended ?? null,
          // Eine neue Zusage stellt sich hinten an; eine bestehende behaelt ihren Platz.
          respondedAt: status === vorher?.status ? (vorher?.respondedAt ?? null) : status === "PENDING" ? null : jetzt,
        },
      ];
    });
    const name = p.firstName || p.name;
    const r = await zusageSetzen(eventId, occurrence, p.id, status);
    const fehler = errorOf(r);
    if (fehler || !r.success) {
      setAntworten(sicherung);
      toast.error(fehler ?? "Das hat nicht geklappt.");
      return;
    }
    if (status === "PENDING") toast(`${name}: Antwort zurückgesetzt`);
    else if (r.warteplatz) toast.success(`${name}: Zusage eingetragen`, { description: `Platz ${r.warteplatz} auf der Warteliste.` });
    else toast.success(`${name}: ${RSVP_STATUS[status].label} eingetragen`);
    const nachId = r.nachgerueckt ?? (warVorne && status !== "YES" ? ersterWartend : null);
    if (nachId) {
      const nach = personen.find((x) => x.id === nachId);
      if (nach) toast.success(`${nach.name} rückt von der Warteliste nach`, { description: "Die Nachricht dazu kommt mit dem Nachrichtenmodul." });
    }
  }

  async function erinnern() {
    const offen = personen.filter((p) => fachVon(p.id) === "offen");
    const ja = await confirmDialog({
      title: `${offen.length} ${offen.length === 1 ? "Person" : "Personen"} erinnern?`,
      description: `Wer noch nicht geantwortet hat, bekommt eine kurze Nachricht mit Zu- und Absage-Knopf für „${titel}“ am ${tagText}. Eltern Minderjähriger werden mit erinnert.`,
      confirmLabel: "Erinnerung senden",
    });
    if (!ja) return;
    // Versand (Mail/Push, Eltern bei Minderjaehrigen) kommt mit dem Nachrichtenmodul.
    toast("Erinnerungen kommen mit dem Nachrichtenmodul", { description: `${offen.length} ${offen.length === 1 ? "Person wäre" : "Personen wären"} dran — bis dahin bitte über die Liste nachfassen.` });
  }

  function herunterladen() {
    const zeilen = [["Name", "Mitgliedsnummer", "Antwort", "Kommentar", "Geantwortet"]];
    for (const p of personen) {
      const a = auswertung.jePerson.get(p.id);
      const f = fachVon(p.id);
      zeilen.push([p.name, p.memberNumber ?? "", FAECHER.find((x) => x.key === f)!.label, a?.comment ?? "", a?.respondedAt ? `${tagVon(a.respondedAt)} ${uhrzeit(a.respondedAt)}` : ""]);
    }
    const csv = zeilen.map((z) => z.map((f) => `"${f.replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `zusagen-${titel.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-")}-${tagText.replace(/\W+/g, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Liste heruntergeladen", { description: "Nur für den internen Gebrauch — enthält Namen und Kommentare." });
  }

  const faecher = FAECHER.filter((f) => (f.key !== "warteliste" || max) && (f.key !== "vielleicht" || vielleichtErlaubt || z.maybe > 0));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
          <div className="min-w-0 flex-1">
            <p className="text-body-sm text-text-primary font-medium">
              {abgesagt ? "Abgesagt — Antworten sind geschlossen." : fristVorbei ? "Antwortfrist abgelaufen" : `Antworten bis ${formatTagMitWochentag(tagVon(frist))}, ${uhrzeit(frist)} Uhr`}
              {!abgesagt && !fristVorbei && <span className="text-text-tertiary font-normal"> · {relativTage(tagVon(frist), heute)}</span>}
            </p>
            <p className="text-caption text-text-tertiary">
              {max ? `Höchstens ${max} Teilnehmende — wer danach zusagt, steht auf der Warteliste und rückt bei Absagen nach.` : "Ohne Teilnehmergrenze."}
              {fristVorbei && !abgesagt ? " Nach der Frist trägt nur noch die Übungsleitung ein." : ""}
            </p>
          </div>
          {max && (
            <div className="w-full sm:w-48">
              <div className="text-caption text-text-secondary mb-1 flex justify-between tabular-nums">
                <span>Plätze</span>
                <span>
                  {Math.min(z.yes, max)} / {max}
                </span>
              </div>
              <div className="bg-bg-tertiary h-1.5 overflow-hidden rounded-full">
                <div className={cn("h-full rounded-full", z.yes >= max ? "bg-warning" : "bg-success")} style={{ width: `${Math.min(100, (z.yes / max) * 100)}%` }} />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={erinnern} disabled={z.pending === 0 || abgesagt || nurLesen}>
              <BellRing className="h-4 w-4" aria-hidden />
              Offene erinnern
            </Button>
            <Button variant="ghost" onClick={() => setEinladen(true)} disabled={abgesagt || nurLesen}>
              <UserPlus className="h-4 w-4" aria-hidden />
              Einladen
            </Button>
            <Button variant="ghost" onClick={herunterladen}>
              <Download className="h-4 w-4" aria-hidden />
              <span className="max-sm:sr-only">Liste</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Schiene>
          <Zaehlknopf label="Alle" anzahl={personen.length} aktiv={!fach} onClick={() => setFach("")} />
          <Fuge />
          {faecher.map((f) => (
            <Zaehlknopf key={f.key} label={f.label} anzahl={zahl[f.key]} aktiv={fach === f.key} onClick={() => setFach(fach === f.key ? "" : f.key)} />
          ))}
        </Schiene>
        <div className="relative max-md:min-w-0 max-md:flex-1">
          <Search className="text-text-tertiary pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" aria-hidden />
          <Input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name suchen …" aria-label="Name suchen" className="h-9 w-56 pl-8 max-md:w-full" />
        </div>
      </div>

      {gezeigt.length === 0 ? (
        <Card>
          <CardContent className="p-5">
            <EmptyState title={suche ? `Niemand heißt „${suche}“` : "Hier steht niemand"} description={fach ? "In diesem Fach ist gerade niemand." : "Zu diesem Termin ist noch niemand eingeladen."} />
          </CardContent>
        </Card>
      ) : (
        <div className="-mx-3">
          <Kartenstapel>
            {FAECHER.map((f) => {
              const liste = gezeigt.filter((p) => fachVon(p.id) === f.key);
              if (!liste.length) return null;
              if (f.key === "warteliste") liste.sort((a, b) => auswertung.warteliste.indexOf(a.id) - auswertung.warteliste.indexOf(b.id));
              return (
                <section key={f.key} className={LISTENKARTE}>
                  <header className={cn("bg-bg-head flex items-center gap-2 px-4 py-2", KARTENRAND)}>
                    <h3 className="text-body-sm text-text-primary font-semibold">{f.label}</h3>
                    <span className="text-caption text-text-tertiary tabular-nums">{liste.length}</span>
                  </header>
                  <ul className="divide-border-subtle divide-y">
                    {liste.map((p) => (
                      <Zeile
                        key={p.id}
                        p={p}
                        antwort={auswertung.jePerson.get(p.id) ?? null}
                        platz={f.key === "warteliste" ? auswertung.warteliste.indexOf(p.id) + 1 : null}
                        vielleicht={vielleichtErlaubt}
                        gesperrt={abgesagt || nurLesen}
                        onSetzen={(s) => setzen(p, s)}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}
          </Kartenstapel>
        </div>
      )}

      {einladen && (
        <EinladenDialog
          schonDa={personen.map((p) => p.id)}
          kontakte={kontakte}
          onClose={() => setEinladen(false)}
          onEinladen={async (neu) => {
            const r = await personenEinladen(eventId, neu.map((p) => p.id));
            const fehler = errorOf(r);
            if (fehler) return void toast.error(fehler);
            setPersonen((liste) => [...liste, ...neu]);
            toast.success(`${neu.length} ${neu.length === 1 ? "Person" : "Personen"} eingeladen`, { description: "Gilt für den ganzen Termin, bei Serien für jedes Vorkommen." });
          }}
        />
      )}
    </div>
  );
}

function Zeile({
  p,
  antwort,
  platz,
  vielleicht,
  gesperrt,
  onSetzen,
}: {
  p: PersonKurz;
  antwort: AntwortView | null;
  platz: number | null;
  vielleicht: boolean;
  gesperrt: boolean;
  onSetzen: (s: RsvpStatus) => void;
}) {
  const status = antwort?.status ?? "PENDING";
  const knoepfe: { s: RsvpStatus; label: string; icon: typeof Check; an: string }[] = [
    { s: "YES", label: "Zusage", icon: Check, an: "bg-success-subtle text-success" },
    ...(vielleicht || status === "MAYBE" ? [{ s: "MAYBE" as const, label: "Vielleicht", icon: HelpCircle, an: "bg-warning-subtle text-warning" }] : []),
    { s: "NO", label: "Absage", icon: X, an: "bg-danger-subtle text-danger" },
  ];
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
      <Avatar firstName={p.firstName} lastName={p.lastName} size="sm" />
      <div className="min-w-0 flex-1 basis-40">
        <p className="flex flex-wrap items-center gap-x-2">
          <Link href={`/mitglieder/${p.id}`} className="text-body-sm text-text-primary hover:text-accent font-medium">
            {p.name}
          </Link>
          {p.funktion && <span className="text-caption text-text-tertiary">{p.funktion}</span>}
          {platz && <TonBadge ton="warning">Platz {platz}</TonBadge>}
          {p.gast && <TonBadge ton="info">Gast</TonBadge>}
          {!p.consentInternet && (
            <Tooltip content="Keine Einwilligung „Internet und Social Media“ — erscheint in keiner öffentlichen Liste">
              <EyeOff className="text-text-tertiary h-3.5 w-3.5" aria-label="Nicht öffentlich" />
            </Tooltip>
          )}
        </p>
        <p className="text-caption text-text-tertiary">
          {antwort?.comment ? <span className="text-text-secondary">„{antwort.comment}“</span> : null}
          {antwort?.comment && antwort?.respondedAt ? " · " : null}
          {antwort?.respondedAt ? <span className="tabular-nums">{formatTagMitWochentag(tagVon(antwort.respondedAt))}, {uhrzeit(antwort.respondedAt)}</span> : status === "PENDING" ? "noch keine Antwort" : null}
          {p.minderjaehrig ? " · antwortet über die Eltern" : ""}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1" role="group" aria-label={`Antwort für ${p.name} eintragen`}>
        {knoepfe.map((k) => {
          const an = status === k.s;
          const Icon = k.icon;
          return (
            <button
              key={k.s}
              type="button"
              disabled={gesperrt}
              aria-pressed={an}
              title={an ? `${k.label} — noch einmal tippen setzt zurück` : `${k.label} eintragen`}
              onClick={() => onSetzen(an ? "PENDING" : k.s)}
              className={cn(
                "duration-fast grid h-8 w-8 place-items-center rounded-lg transition-colors disabled:opacity-40",
                an ? k.an : "text-text-tertiary hover:bg-bg-secondary hover:text-text-primary",
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
              <span className="sr-only">{k.label}</span>
            </button>
          );
        })}
      </div>
    </li>
  );
}

function EinladenDialog({
  schonDa,
  kontakte,
  onClose,
  onEinladen,
}: {
  schonDa: string[];
  kontakte?: ContactView[];
  onClose: () => void;
  onEinladen: (p: PersonKurz[]) => void | Promise<void>;
}) {
  const [ids, setIds] = useState<string[]>([]);
  const kurz = (liste: string[]): PersonKurz[] =>
    kontakte
      ? liste
          .map((id) => kontakte.find((c) => c.id === id))
          .filter((c): c is ContactView => Boolean(c))
          .map((c) => ({ id: c.id, name: c.displayName, firstName: c.firstName ?? "", lastName: c.lastName ?? c.displayName, memberNumber: c.memberNumber, funktion: null, consentInternet: c.consentInternet, minderjaehrig: false }))
      : mockPersonen(liste);
  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Weitere Personen einladen</DialogTitle>
        <DialogDescription>Zusätzlich zu den Gruppen des Termins — etwa ein Gastspieler oder die Kassenprüfer zur Vorstandssitzung.</DialogDescription>
      </DialogHeader>
      <DialogContent>
        <KontaktAuswahl mehrfach values={ids.filter((i) => !schonDa.includes(i))} onChangeMany={setIds} placeholder="Personen suchen …" kontakte={kontakte} />
      </DialogContent>
      <DialogFooter>
        <DialogCancel />
        <Button
          disabled={ids.length === 0}
          onClick={async () => {
            await onEinladen(kurz(ids.filter((i) => !schonDa.includes(i))));
            onClose();
          }}
        >
          Einladen
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
